import React, { useState, useEffect, useMemo } from 'react';
import { formatCurrency, parseCurrency } from '../utils/formatters';
import { CIEN_CUADRAS_BLUE, CIEN_CUADRAS_BUTTON_BLUE } from '../firebase/firebaseConfig';
import { CalculatorIcon, ChevronDownIcon, InformationCircleIcon, CashIcon, MortgageIcon, KeyIcon } from './Icons';
import { useAppConfig } from '../hooks/useAppConfig';

interface PurchaseSimulatorProps {
  initialValue: number;
}

type Modality = 'contado' | 'credito' | 'leasing' | null;

// --- START: NEW TYPES AND CONSTANTS FOR DETAILED COSTS ---
interface CostItem {
    name: string;
    value: number;
    description: string;
    for: 'buyer' | 'seller';
    notes?: string;
}
interface ClosingCosts {
    buyerTotal: number;
    sellerTotal: number;
    buyerCosts: CostItem[];
    sellerCosts: CostItem[];
}
const NOTARY_FEE_RATE = 0.0054;
const BENEFICENCIA_TAX_RATE = 0.01;
const REGISTRY_FEE_RATE = 0.0067;
const WITHHOLDING_TAX_RATE = 0.01;
const MORTGAGE_COST_RATE_ESTIMATE = 0.005; // 0.5% estimate on loan amount for mortgage constitution costs
// --- END: NEW TYPES AND CONSTANTS ---

const CurrencyInput: React.FC<{
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ label, id, value, onChange, disabled }) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, '');
    onChange(rawValue);
  };
  const formattedValue = value ? new Intl.NumberFormat('es-CO').format(parseInt(value, 10)) : '';

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="mt-1 relative rounded-md shadow-sm">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <span className="text-gray-500 sm:text-sm">$</span>
        </div>
        <input
          type="text"
          id={id}
          className="block w-full rounded-md border-gray-300 pl-7 pr-4 focus:border-sky-500 focus:ring-sky-500 sm:text-sm disabled:bg-gray-100"
          placeholder="0"
          value={formattedValue}
          onChange={handleInputChange}
          disabled={disabled}
          inputMode="numeric"
        />
      </div>
    </div>
  );
};

const Timeline: React.FC<{ steps: { title: string; description: string }[] }> = ({ steps }) => (
    <div className="mt-4">
        {steps.map((step, index) => (
            <div key={index} className="flex">
                <div className="flex flex-col items-center mr-4">
                    <div>
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-600 text-white font-bold">{index + 1}</div>
                    </div>
                    {index < steps.length - 1 && <div className="h-full w-px bg-gray-300"></div>}
                </div>
                <div className="pb-8">
                    <p className="mb-1 text-md font-semibold text-gray-800">{step.title}</p>
                    <p className="text-sm text-gray-600">{step.description}</p>
                </div>
            </div>
        ))}
    </div>
);

// --- START: NEW COST BREAKDOWN COMPONENT ---
const CostBreakdownCard: React.FC<{ title: string; costs: CostItem[]; total: number; note?: string }> = ({ title, costs, total, note }) => (
    <div className="bg-white p-4 rounded-lg shadow-sm space-y-3 h-full flex flex-col">
        <h4 className="text-md font-semibold text-center text-sky-800">{title}</h4>
        {note && <p className="text-xs text-center text-orange-600 bg-orange-50 p-2 rounded-md">{note}</p>}
        <div className="space-y-2 flex-grow">
            {costs.map((item, index) => (
                <div key={index} className="flex justify-between items-center text-sm group relative">
                    <div className="flex items-center">
                        <span className="text-gray-700">{item.name}</span>
                        <div className="ml-1.5 cursor-help">
                            <InformationCircleIcon className="w-4 h-4 text-gray-400" />
                            <div className="absolute bottom-full left-0 mb-2 w-72 bg-gray-800 text-white text-xs rounded py-2 px-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 shadow-lg">
                                {item.description}
                                {item.notes && <em className="block mt-1 text-orange-300">{item.notes}</em>}
                            </div>
                        </div>
                    </div>
                    <span className="font-medium text-gray-800">{formatCurrency(item.value)}</span>
                </div>
            ))}
        </div>
        <div className="border-t pt-3 mt-auto flex justify-between items-center font-bold text-md">
            <span className="text-sky-900">Total Aprox.</span>
            <span className="text-sky-900">{formatCurrency(total)}</span>
        </div>
    </div>
);
// --- END: NEW COST BREAKDOWN COMPONENT ---


export const PurchaseSimulator: React.FC<PurchaseSimulatorProps> = ({ initialValue }) => {
    const { appConfig } = useAppConfig();
    const [isOpen, setIsOpen] = useState(false);
    const [propertyValue, setPropertyValue] = useState(String(initialValue || 0));
    const [savings, setSavings] = useState('');
    const [cesantias, setCesantias] = useState('');
    const [selectedModality, setSelectedModality] = useState<Modality>(null);
    const [loanTerm, setLoanTerm] = useState(15);
    
    // --- START: NEW STATE FOR DETAILED COSTS ---
    const [closingCosts, setClosingCosts] = useState<ClosingCosts | null>(null);
    // --- END: NEW STATE ---

    const totalOwnFunds = useMemo(() => {
        return (parseCurrency(savings) || 0) + (parseCurrency(cesantias) || 0);
    }, [savings, cesantias]);

    const numericPropertyValue = useMemo(() => parseCurrency(propertyValue) || 0, [propertyValue]);

    const annualRate = useMemo(() => {
        const ratesConfig = appConfig?.interest_rates?.value;
        const defaultRates = {
            visThreshold: 213525000,
            credito: { vis: 0.1130, noVis: 0.1225 },
            leasing: { vis: 0.1100, noVis: 0.1170 }
        };

        const rates = ratesConfig || defaultRates;
        const threshold = rates.visThreshold;
        
        if (!numericPropertyValue || !selectedModality) return 0;
        
        if (selectedModality === 'credito') {
            return numericPropertyValue <= threshold ? rates.credito.vis : rates.credito.noVis;
        }
        if (selectedModality === 'leasing') {
            return numericPropertyValue <= threshold ? rates.leasing.vis : rates.leasing.noVis;
        }
        return 0;
    }, [numericPropertyValue, selectedModality, appConfig]);

    const loanAmount = useMemo(() => {
        if (!numericPropertyValue || !selectedModality || !['credito', 'leasing'].includes(selectedModality)) {
            return 0;
        }

        const maxFinancePercent = selectedModality === 'credito' ? 0.70 : 0.80;
        const maxLoanAmount = numericPropertyValue * maxFinancePercent;
        
        const neededLoan = numericPropertyValue - totalOwnFunds;

        if (neededLoan <= 0) {
            return 0;
        }

        return Math.min(neededLoan, maxLoanAmount);

    }, [numericPropertyValue, totalOwnFunds, selectedModality]);

    const monthlyPayment = useMemo(() => {
        if (!numericPropertyValue || loanTerm <= 0 || !['credito', 'leasing'].includes(selectedModality || '') || annualRate === 0 || loanAmount <= 0) return 0;
        const monthlyRate = Math.pow(1 + annualRate, 1 / 12) - 1;
        const termInMonths = loanTerm * 12;
        if (monthlyRate === 0) return loanAmount / termInMonths;
        return (loanAmount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -termInMonths));
    }, [numericPropertyValue, loanTerm, selectedModality, annualRate, loanAmount]);

    // --- START: NEW EFFECT TO CALCULATE DETAILED COSTS ---
    useEffect(() => {
        if (!selectedModality || numericPropertyValue <= 0) {
            setClosingCosts(null);
            return;
        }

        const buyerCosts: CostItem[] = [];
        const sellerCosts: CostItem[] = [];

        const notaryFeesTotal = numericPropertyValue * NOTARY_FEE_RATE;
        const beneficencia = numericPropertyValue * BENEFICENCIA_TAX_RATE;
        const registro = numericPropertyValue * REGISTRY_FEE_RATE;
        const retencion = numericPropertyValue * WITHHOLDING_TAX_RATE;
        
        const descNotary = "El costo de la escrituración equivale aproximadamente al 0.54% del valor del inmueble. Por costumbre, este valor se divide en partes iguales (50/50) entre el comprador y el vendedor.";
        const descBeneficencia = "Es un impuesto departamental que se paga para poder registrar la escritura. La tarifa varía ligeramente según el departamento, pero suele rondar el 1% del valor de la venta.";
        const descRegistro = "Corresponde a la inscripción de la escritura en la Oficina de Registro de Instrumentos Públicos para que el comprador figure como el nuevo propietario legal. Este costo es de aproximadamente el 0.67% del valor del inmueble.";
        const descRetencion = "Corresponde al 1% del valor de la venta del inmueble. Este es un anticipo del impuesto de renta y el notario lo retiene para pagarlo a la DIAN.";

        if (selectedModality === 'contado' || selectedModality === 'credito') {
            buyerCosts.push({ name: "Derechos Notariales (50%)", value: notaryFeesTotal / 2, description: descNotary, for: 'buyer' });
            buyerCosts.push({ name: "Impuesto de Beneficencia", value: beneficencia, description: descBeneficencia, for: 'buyer' });
            buyerCosts.push({ name: "Derechos de Registro", value: registro, description: descRegistro, for: 'buyer' });
            
            sellerCosts.push({ name: "Derechos Notariales (50%)", value: notaryFeesTotal / 2, description: descNotary, for: 'seller' });
            sellerCosts.push({ name: "Retención en la Fuente (1%)", value: retencion, description: descRetencion, for: 'seller' });

            if (selectedModality === 'credito') {
                const creditLoanAmount = loanAmount;
                if (creditLoanAmount > 0) {
                    const mortgageCosts = creditLoanAmount * MORTGAGE_COST_RATE_ESTIMATE;
                    buyerCosts.push({
                        name: "Gastos de Hipoteca",
                        value: mortgageCosts,
                        description: "Costos notariales y de registro derivados de la constitución de la hipoteca a favor del banco, asumidos en su totalidad por el comprador.",
                        for: 'buyer'
                    });
                }
            }
        }

        if (selectedModality === 'leasing') {
             buyerCosts.push({
                name: "Gastos Totales de Escrituración",
                value: notaryFeesTotal + beneficencia + registro,
                description: "Al ejercer la opción de compra, el locatario (quien tenía el leasing y ahora compra) asume la totalidad de los gastos notariales y de registro, que incluyen derechos notariales, beneficencia y registro.",
                for: 'buyer',
                notes: 'Este costo se paga al final del contrato de leasing, al ejercer la opción de compra.'
             });
        }
        
        setClosingCosts({
            buyerCosts,
            sellerCosts,
            buyerTotal: buyerCosts.reduce((acc, cost) => acc + cost.value, 0),
            sellerTotal: sellerCosts.reduce((acc, cost) => acc + cost.value, 0),
        });

    }, [numericPropertyValue, selectedModality, loanAmount]);
    // --- END: NEW EFFECT ---

    const handleSelectModality = (modality: Modality) => {
        setSelectedModality(prev => prev === modality ? null : modality);
    };

    const renderResults = () => {
        if (!selectedModality) return null;

        switch(selectedModality) {
            case 'contado':
                const totalToPay = numericPropertyValue + (closingCosts?.buyerTotal || 0);
                const balance = totalOwnFunds - totalToPay;
                const timelineStepsContado = [
                    { title: "Firma de Promesa", description: "Acuerdo de un primer pago, generalmente entre el 10% y 30% del valor del inmueble." },
                    { title: "Firma de Escrituras", description: "Pago de Gastos Notariales y de Registro para formalizar la propiedad." },
                    { title: "Entrega del Inmueble", description: "Pago del saldo restante del inmueble contra entrega de las llaves." }
                ];
                return (
                    <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-center" style={{color: CIEN_CUADRAS_BLUE}}>Resumen de Compra de Contado</h3>
                        <div className="bg-white p-4 rounded-lg shadow space-y-2">
                           <div className="flex justify-between text-sm"><span className="text-gray-600">Valor del Inmueble</span><span className="font-medium">{formatCurrency(numericPropertyValue)}</span></div>
                           <div className="flex justify-between text-sm"><span className="text-gray-600">Costos de Cierre (Comprador)</span><span className="font-medium">{formatCurrency(closingCosts?.buyerTotal || 0)}</span></div>
                           <div className="flex justify-between font-bold border-t pt-2 mt-2"><span className="text-gray-800">Total a Pagar</span><span>{formatCurrency(totalToPay)}</span></div>
                        </div>
                         <div className="bg-white p-4 rounded-lg shadow space-y-2">
                           <div className="flex justify-between text-sm"><span className="text-gray-600">Total Aportes Propios</span><span className="font-medium">{formatCurrency(totalOwnFunds)}</span></div>
                           <div className={`flex justify-between font-bold border-t pt-2 mt-2 ${balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                <span>{balance >= 0 ? 'Sobrante' : 'Faltante'}</span>
                                <span>{formatCurrency(balance)}</span>
                            </div>
                        </div>
                        {closingCosts && (
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2 mt-4">4. Desglose de Costos de Cierre y Formalización</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <CostBreakdownCard title="Asumidos por Comprador" costs={closingCosts.buyerCosts} total={closingCosts.buyerTotal} />
                                    <CostBreakdownCard title="Asumidos por Vendedor" costs={closingCosts.sellerCosts} total={closingCosts.sellerTotal} />
                                </div>
                            </div>
                        )}
                        <Timeline steps={timelineStepsContado} />
                    </div>
                );
            case 'credito':
            case 'leasing':
                const isCredito = selectedModality === 'credito';
                const downPaymentPercent = isCredito ? 0.30 : 0.20;
                const requiredDownPayment = numericPropertyValue * downPaymentPercent;
                const downPaymentDifference = totalOwnFunds - requiredDownPayment;
                const financePercentDecimal = numericPropertyValue > 0 ? loanAmount / numericPropertyValue : 0;
                const appraisalCost = 300000;
                const titleStudyCost = 200000;
                const totalCreditExpenses = appraisalCost + titleStudyCost;
                const leasingPurchaseOption = numericPropertyValue * 0.05;

                const timelineStepsCredito = [
                    { title: "Ahora", description: `Reunir la Cuota Inicial (${formatCurrency(requiredDownPayment)}) y los costos de cierre y crédito (${formatCurrency((closingCosts?.buyerTotal || 0) + totalCreditExpenses)}).` },
                    { title: "Durante la Aprobación", description: `Pago del avalúo (${formatCurrency(appraisalCost)}) y estudio de títulos (${formatCurrency(titleStudyCost)}).`},
                    { title: "En la Notaría", description: `Pago de gastos notariales y de registro al firmar las escrituras.` },
                    { title: `Mensualmente por ${loanTerm} años`, description: `Pago de la cuota mensual del crédito.` }
                ];
                 const timelineStepsLeasing = [
                    { title: "Ahora", description: `Reunir el Canon Inicial (${formatCurrency(requiredDownPayment)}) y los otros gastos del crédito (${formatCurrency(totalCreditExpenses)}).` },
                    { title: "Durante la Aprobación", description: `Pago del avalúo (${formatCurrency(appraisalCost)}) y estudio de títulos (${formatCurrency(titleStudyCost)}).`},
                    { title: "En la Notaría", description: `En esta modalidad no se pagan gastos de escrituración al inicio.` },
                    { title: `Mensualmente por ${loanTerm} años`, description: `Pago del canon mensual del leasing.` },
                    { title: `Al finalizar el plazo`, description: `Pago de la Opción de Compra (${formatCurrency(leasingPurchaseOption)}) y los Gastos de Escrituración para ser propietario.` }
                ];

                return (
                    <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-center" style={{color: CIEN_CUADRAS_BLUE}}>
                            Simulación con {isCredito ? "Crédito Hipotecario" : "Leasing Habitacional"}
                        </h3>
                        <div className="bg-white p-4 rounded-lg shadow">
                            <h4 className="font-semibold text-gray-800 mb-2">1. {isCredito ? 'Cuota Inicial' : 'Canon Inicial'} (Requerido {downPaymentPercent * 100}%)</h4>
                            <div className="flex justify-between text-sm"><span className="text-gray-600">Valor Requerido</span><span className="font-medium">{formatCurrency(requiredDownPayment)}</span></div>
                            <div className="flex justify-between text-sm"><span className="text-gray-600">Total Aportes Propios</span><span className="font-medium">{formatCurrency(totalOwnFunds)}</span></div>
                            <div className={`text-center p-2 mt-3 rounded text-sm ${downPaymentDifference >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {downPaymentDifference >= 0 
                                    ? "¡Excelente! Tus aportes cubren el valor requerido." 
                                    : `Necesitas completar ${formatCurrency(Math.abs(downPaymentDifference))} para el valor requerido.`
                                }
                            </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow">
                            <h4 className="font-semibold text-gray-800 mb-1">2. Monto a Financiar ({ (financePercentDecimal * 100).toFixed(0) }%)</h4>
                            <p className="text-2xl font-bold text-sky-700">{formatCurrency(loanAmount)}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow">
                            <h4 className="font-semibold text-gray-800 mb-2">3. Simulación del {isCredito ? 'Crédito' : 'Leasing'}</h4>
                            <div className="space-y-3">
                                <div>
                                    <label htmlFor="loanTerm" className="block text-sm font-medium text-gray-700">Plazo del {isCredito ? 'Crédito' : 'Leasing'}: <span className="font-bold text-sky-700">{loanTerm} años</span></label>
                                    <input id="loanTerm" type="range" min="5" max="20" step="1" value={loanTerm} onChange={e => setLoanTerm(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-sky-600"/>
                                </div>
                                <div className="text-sm text-gray-600 flex items-center">Tasa de Interés (Estimada): <span className="font-bold text-gray-700 mx-1">{(annualRate * 100).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}% E.A.</span> <InformationCircleIcon className="w-4 h-4" /></div>
                                <div className="text-center bg-sky-50 p-3 rounded-lg">
                                    <p className="text-md text-sky-800">{isCredito ? 'Cuota Mensual Estimada' : 'Canon Mensual Estimado'}</p>
                                    <p className="text-3xl font-bold text-sky-700">{formatCurrency(monthlyPayment)}</p>
                                    <p className="text-xs text-gray-500 mt-1">No incluye seguros de vida y de incendio.</p>
                                </div>
                            </div>
                        </div>
                         {closingCosts && (
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2 mt-4">4. Desglose de Costos de Cierre y Formalización</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <CostBreakdownCard title="Asumidos por Comprador" costs={closingCosts.buyerCosts} total={closingCosts.buyerTotal} note={!isCredito ? "Estos costos se pagan al final del contrato, al ejercer la opción de compra." : undefined} />
                                    {isCredito && <CostBreakdownCard title="Asumidos por Vendedor" costs={closingCosts.sellerCosts} total={closingCosts.sellerTotal} />}
                                </div>
                            </div>
                        )}
                        <div className="bg-white p-4 rounded-lg shadow mt-4">
                             <h4 className="font-semibold text-gray-800 mb-2">5. Otros Gastos del Crédito (Comprador)</h4>
                             {!isCredito && <div className="flex justify-between text-sm"><span className="text-gray-600">Opción de Compra (Estimada 5%)</span><span className="font-medium">{formatCurrency(leasingPurchaseOption)}</span></div>}
                             <div className="flex justify-between text-sm"><span className="text-gray-600">Avalúo (Estimado)</span><span className="font-medium">{formatCurrency(appraisalCost)}</span></div>
                             <div className="flex justify-between text-sm"><span className="text-gray-600">Estudio de Títulos (Estimado)</span><span className="font-medium">{formatCurrency(titleStudyCost)}</span></div>
                             <div className="flex justify-between font-bold border-t pt-2 mt-2"><span className="text-gray-800">Total Otros Gastos</span><span>{formatCurrency(totalCreditExpenses + (isCredito ? 0 : leasingPurchaseOption))}</span></div>
                        </div>
                        <Timeline steps={isCredito ? timelineStepsCredito : timelineStepsLeasing} />
                    </div>
                )
        }
    }


  return (
    <div className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center p-4 text-left"
        aria-expanded={isOpen}
      >
        <div className="flex items-center">
            <CalculatorIcon className="w-6 h-6 mr-3 text-sky-700"/>
            <span className="font-semibold text-lg" style={{color: CIEN_CUADRAS_BLUE}}>
                Simulador de Compra Inteligente
            </span>
        </div>
        <ChevronDownIcon className={`w-6 h-6 text-sky-700 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-4 border-t border-gray-200 space-y-6">
            {/* Step 1: Inputs */}
            <div className="bg-white p-4 rounded-lg shadow">
                 <h3 className="font-semibold text-gray-800 mb-3">Paso 1: Datos Fundamentales</h3>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <CurrencyInput label="Valor del Inmueble" id="sim-property-value" value={propertyValue} onChange={setPropertyValue} />
                     <CurrencyInput label="Ahorros Disponibles" id="sim-savings" value={savings} onChange={setSavings} />
                     <CurrencyInput label="Cesantías" id="sim-cesantias" value={cesantias} onChange={setCesantias} />
                    <div className="md:col-span-2 bg-gray-100 p-3 rounded-md text-center">
                        <p className="text-sm font-medium text-gray-600">Total Aportes Propios</p>
                        <p className="text-xl font-bold text-sky-700">{formatCurrency(totalOwnFunds)}</p>
                    </div>
                 </div>
            </div>

            {/* Step 2: Modality Selection */}
            <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="font-semibold text-gray-800 mb-3">Paso 2: Modalidad de Compra</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <button onClick={() => handleSelectModality('contado')} className={`p-4 border-2 rounded-lg text-center transition-all duration-200 ${selectedModality === 'contado' ? 'border-sky-500 bg-sky-50 ring-2 ring-sky-500' : 'border-gray-200 hover:border-sky-400 hover:bg-gray-50'}`}>
                        <CashIcon className="w-8 h-8 mx-auto text-sky-600 mb-2"/>
                        <p className="font-semibold text-gray-800">De Contado</p>
                        <p className="text-xs text-gray-500 mt-1">Paga el valor total con tus recursos.</p>
                    </button>
                     <button onClick={() => handleSelectModality('credito')} className={`p-4 border-2 rounded-lg text-center transition-all duration-200 ${selectedModality === 'credito' ? 'border-sky-500 bg-sky-50 ring-2 ring-sky-500' : 'border-gray-200 hover:border-sky-400 hover:bg-gray-50'}`}>
                        <MortgageIcon className="w-8 h-8 mx-auto text-sky-600 mb-2"/>
                        <p className="font-semibold text-gray-800">Crédito Hipotecario</p>
                        <p className="text-xs text-gray-500 mt-1">Financia hasta el 70% del valor del inmueble.</p>
                    </button>
                     <button onClick={() => handleSelectModality('leasing')} className={`p-4 border-2 rounded-lg text-center transition-all duration-200 ${selectedModality === 'leasing' ? 'border-sky-500 bg-sky-50 ring-2 ring-sky-500' : 'border-gray-200 hover:border-sky-400 hover:bg-gray-50'}`}>
                        <KeyIcon className="w-8 h-8 mx-auto text-sky-600 mb-2"/>
                        <p className="font-semibold text-gray-800">Leasing Habitacional</p>
                        <p className="text-xs text-gray-500 mt-1">Financia hasta el 80% con opción de compra.</p>
                    </button>
                </div>
            </div>
            
            {/* Step 3: Results */}
            {selectedModality && (
                <div className="bg-gray-100 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-3 text-center">Paso 3: Visualización de Resultados</h3>
                    {renderResults()}
                </div>
            )}

            {selectedModality && (
                 <div className="text-center pt-4 border-t">
                     <p className="text-xs text-gray-500 max-w-xl mx-auto">
                        *Esta es una simulación y los valores son aproximados. Las tasas y condiciones finales serán definidas por la entidad financiera.
                    </p>
                 </div>
            )}
        </div>
      </div>
    </div>
  );
};